import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import { PresenterDashboard } from "@/components/slides/presenter-dashboard";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Init handoff via URL params — bulletproof. sessionStorage isn't reliably
// copied to popups in modern Chrome (COOP), and localStorage handoff broke
// under Vite HMR when the editor's bundle held a stale reference to the
// writer fn. URL params are in the popup's URL when it loads — no IPC, no
// storage timing, no HMR caveats. Tradeoff: workspace `tkn=` in previewUrl
// lands in the popup's browser history. That token is short-lived (15min
// prod, 24h dev) and the same one is in the editor's IDE URL anyway.
type InitData = { previewUrl: string; devContainerId: string };

// Base64-encode JSON so reserved URL chars in `previewUrl` (?, &, =) don't
// collide with our own query-string structure.
export function encodePresenterInit(data: InitData): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(data))));
}

function decodePresenterInit(raw: string): InitData | null {
  try {
    const json = decodeURIComponent(escape(atob(raw)));
    return JSON.parse(json) as InitData;
  } catch {
    return null;
  }
}

export function buildPresenterUrl(
  projectId: string,
  data: InitData,
): string {
  return `/presenter/${projectId}?init=${encodePresenterInit(data)}`;
}

export default function PresenterRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { getIdToken } = useAuth();
  const [init, setInit] = useState<InitData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setError("Missing project ID");
      return;
    }
    const initParam = searchParams.get("init");
    if (!initParam) {
      setError(
        "Presenter view must be launched from the editor. Open the project and click Present → Presenter view.",
      );
      return;
    }
    const data = decodePresenterInit(initParam);
    if (!data) {
      setError("Presenter init data is malformed.");
      return;
    }
    setInit(data);
    // Populate the slides store in this window. The dashboard relies on the
    // store for currentSlideIndex, slides[], and thumbnailRevision.
    void fetchSlideManifest({
      devContainerId: data.devContainerId,
      getIdToken,
    });
    document.title = "Presenter View — OptiDev";
  }, [projectId, searchParams, getIdToken]);

  // (Previously this broadcasted `end-presentation` on `beforeunload` /
  // `pagehide` so closing the presenter window via [×] would also exit the
  // editor's Present overlay. Removed because the listener also fires when
  // window.open() re-navigates a leftover popup from a prior session/HMR
  // reload — broadcasting end-presentation before the new mount renders,
  // and racing the editor's own setIsPresentMode(true). The editor user
  // can still exit the overlay manually via Escape; the explicit End
  // Presentation button still broadcasts end-presentation correctly.)

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-950 text-white p-8">
        <div className="max-w-md text-center">
          <p className="text-lg font-medium mb-2">
            Presenter view unavailable
          </p>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!init) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-950">
        <LoadingSpinner message="Loading presenter view…" />
      </div>
    );
  }

  return (
    <PresenterDashboard
      previewUrl={init.previewUrl}
      devContainerId={init.devContainerId}
      projectId={projectId!}
      getIdToken={getIdToken}
      onClose={() => window.close()}
    />
  );
}
